import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { ColumnDef } from "@tanstack/react-table";
import { toast } from "react-toastify";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Combobox } from "@/components/ui/combobox";
import { GenericTable } from "@/components/shared/GenericTable";
import { InfoAlert } from "@/components/shared/Alerts";
import { ModalContentError } from "@/components/shared/Modal";
import { Loader } from "@/components/ui/loader";
import { RawSpecValue, SpecListMinimal, SpecListVersionMinimal, SpecValue } from "@/types/specList";
import {
  useCreateSpecList,
  useSpecListsForSpecificMask,
  useSpecValuesAvailableForMask,
} from "@/hooks/useSpecLists";
import { deepEqual } from "@/lib/utils";
import { SelectSpecValuesTableToolbar } from "../SelectSpecValuesTableToolbar";
import SpecValueCell, { SelectedSpecValue } from "../SpecValueCell";

type FormData = {
  name: string;
  maskId: string;
  masterVersionId: string;
};

type FormStepOne = {
  existingNames: string[];
  formData: FormData;
  masterVersions: SpecListVersionMinimal[];
  onOpenChange: (open: boolean) => void;
  setChoice: (choice: "" | "derived" | "custom") => void;
  setFormData: (data: FormData) => void;
  setStep: (step: number) => void;
};
const FormStepOne = ({
  existingNames,
  formData,
  masterVersions,
  onOpenChange,
  setChoice,
  setFormData,
  setStep,
}: FormStepOne) => {
  const [nameError, setNameError] = useState<string>("");
  const canProceedToStep2 = formData.name && formData.maskId && formData.masterVersionId;

  const handleNext = () => {
    if (existingNames.some((n) => n.toLowerCase() === formData.name.trim().toLowerCase())) {
      setNameError(
        "A spec list with this name already exists for this mask. Try a different name.",
      );
      return;
    }
    setNameError("");
    setStep(2);
  };

  const onBack = () => {
    setChoice("");
  };

  return (
    <div className="space-y-4 flex-1">
      <div className={clsx("grid gap-4", "grid-cols-2")}>
        <div className="space-y-2">
          <Label htmlFor="name">
            Spec List Name <span className="text-red-500">*</span>
          </Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => {
              setFormData({ ...formData, name: e.target.value });
              if (nameError) setNameError("");
            }}
            placeholder="Enter name for new Spec List"
            required
          />
          {nameError && <p className="text-sm text-destructive">{nameError}</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="masterVersionId">
            Master Version <span className="text-red-500">*</span>
          </Label>
          <Combobox
            fullWidth
            modal
            onChange={(value) => setFormData({ ...formData, masterVersionId: value })}
            options={masterVersions.map((mv) => ({
              key: mv.id,
              label: `${mv.specList.name} - v${mv.version}`,
              value: mv.id,
            }))}
            placeholder="Select Master Version"
            searchPlaceholder="Search..."
            value={formData.masterVersionId}
          />
        </div>
      </div>

      <div className="flex justify-between gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onBack}>
          Back
        </Button>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleNext} disabled={!canProceedToStep2}>
            Next: Select Specs and Adjust Values
          </Button>
        </div>
      </div>
    </div>
  );
};

type FormStepTwoProps = {
  formData: FormData;
  isCreatingSpecList: boolean;
  isFetchingSpecValues: boolean;
  onOpenChange: (open: boolean) => void;
  selectedSpecs: SelectedSpecValue[];
  setFormData: (data: FormData) => void;
  setSelectedSpecs: (specs: (prev: SelectedSpecValue[]) => SelectedSpecValue[]) => void;
  setStep: (step: number) => void;
};
const FormStepTwo = ({
  formData,
  isCreatingSpecList,
  isFetchingSpecValues,
  onOpenChange,
  selectedSpecs,
  setFormData,
  setSelectedSpecs,
  setStep,
}: FormStepTwoProps) => {
  const handleSpecSelection = (specId: string, selected: boolean) => {
    setSelectedSpecs((prev) =>
      prev.map((spec) => (spec.id === specId ? { ...spec, selected } : spec)),
    );
  };

  const handleValueChange = (specId: string, field: keyof SpecValue, value: string) => {
    const numValue = value === "" ? undefined : Number.parseFloat(value);

    const addCustomValues = (spec: SelectedSpecValue) => {
      if (spec.id === specId) {
        return {
          ...spec,
          customValues: {
            ...spec.customValues,
            [field]: numValue,
          },
        };
      }

      return spec;
    };

    setSelectedSpecs((prev) => prev.map(addCustomValues));
  };

  const resetForm = () => {
    setStep(1);
    setFormData({
      maskId: formData.maskId,
      masterVersionId: "",
      name: "",
    });
    setSelectedSpecs(() => []);
  };

  const onCancel = () => {
    resetForm();
    onOpenChange(false);
  };

  const canSubmit = selectedSpecs.some((spec) => spec.selected);

  const deviceClasses = [...new Set(selectedSpecs.map((sp) => sp.spec.deviceClass))];
  const conditions = [...new Set(selectedSpecs.map((sp) => sp.spec.condition ?? "None"))];

  const columns: ColumnDef<SelectedSpecValue>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllRowsSelected() || (table.getIsSomeRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => {
            table.toggleAllRowsSelected(!!value);
            setSelectedSpecs((prev) => prev.map((spec) => ({ ...spec, selected: !!value })));
          }}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(checked) => {
            handleSpecSelection(row.original.id, !!checked);
            return row.getToggleSelectedHandler()(checked);
          }}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
    },
    {
      id: "name",
      accessorKey: "spec.name",
      header: "Spec Name",
    },
    {
      id: "condition",
      accessorKey: "spec.condition",
      header: () => "Condition",
      enableGlobalFilter: false,
      cell: ({ row }) => <Badge variant="outline">{row.original.spec.condition}</Badge>,
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      id: "reportName",
      accessorKey: "spec.reportName",
      header: "Report Name",
    },
    {
      id: "legacyNames",
      accessorKey: "spec.legacyNames",
      header: "Legacy Name",
      enableSorting: true,
    },
    {
      id: "dutNames",
      accessorKey: "spec.dutNames",
      header: "DUT Name",
    },
    {
      id: "deviceClass",
      accessorKey: "spec.deviceClass",
      header: () => "Device Class",
      cell: ({ row }) => <Badge variant="outline">{row.original.spec.deviceClass}</Badge>,
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      id: "lsl",
      accessorKey: "lsl",
      header: "LSL",
      enableGlobalFilter: false,
      cell: ({ row }) => SpecValueCell(row.original, "lsl", handleValueChange),
    },
    {
      id: "target",
      accessorKey: "tgt",
      header: "Target",
      enableGlobalFilter: false,
      cell: ({ row }) => SpecValueCell(row.original, "tgt", handleValueChange),
      enableSorting: false,
    },
    {
      id: "usl",
      accessorKey: "usl",
      header: "USL",
      enableGlobalFilter: false,
      cell: ({ row }) => SpecValueCell(row.original, "usl", handleValueChange),
      enableSorting: false,
    },
    {
      id: "jmpYmin",
      accessorKey: "jmpYmin",
      header: "JMP Y Min",
      enableGlobalFilter: false,
      cell: ({ row }) => SpecValueCell(row.original, "jmpYmin", handleValueChange),
      enableSorting: false,
    },
    {
      id: "jmpYmax",
      accessorKey: "jmpYmax",
      header: "JMP Y Max",
      enableGlobalFilter: false,
      cell: ({ row }) => SpecValueCell(row.original, "jmpYmax", handleValueChange),
      enableSorting: false,
    },
    {
      id: "jmpYinc",
      accessorKey: "jmpYinc",
      header: "JMP Y Inc",
      enableGlobalFilter: false,
      cell: ({ row }) => SpecValueCell(row.original, "jmpYinc", handleValueChange),
      enableSorting: false,
    },
    {
      id: "libMedian",
      accessorKey: "libMedian",
      header: "Lib Median",
      enableGlobalFilter: false,
      cell: ({ row }) => SpecValueCell(row.original, "libMedian", handleValueChange),
      enableSorting: false,
    },
    {
      id: "libMean",
      accessorKey: "libMean",
      header: "Lib Mean",
      enableGlobalFilter: false,
      cell: ({ row }) => SpecValueCell(row.original, "libMean", handleValueChange),
      enableSorting: false,
    },
    {
      id: "libStd",
      accessorKey: "libStd",
      header: "Lib Std",
      enableGlobalFilter: false,
      cell: ({ row }) => SpecValueCell(row.original, "libStd", handleValueChange),
      enableSorting: false,
    },
    {
      id: "libQ10",
      accessorKey: "libQ10",
      header: "Lib Q10",
      enableGlobalFilter: false,
      cell: ({ row }) => SpecValueCell(row.original, "libQ10", handleValueChange),
      enableSorting: false,
    },
    {
      id: "libQ90",
      accessorKey: "libQ90",
      header: "Lib Q90",
      enableGlobalFilter: false,
      cell: ({ row }) => SpecValueCell(row.original, "libQ90", handleValueChange),
      enableSorting: false,
    },
    {
      id: "libN",
      accessorKey: "libN",
      header: "Lib N",
      enableGlobalFilter: false,
      cell: ({ row }) => SpecValueCell(row.original, "libN", handleValueChange),
      enableSorting: false,
    },
  ];

  return (
    <div className="space-y-4 flex-1 min-h-0 flex flex-col">
      <div className="flex-1 min-h-0">
        <Card>
          <CardHeader>
            <InfoAlert>
              To update values, please select a specification by clicking the checkbox.
            </InfoAlert>
          </CardHeader>
          <CardContent>
            {isFetchingSpecValues ? (
              <Loader text="Fetching spec values..." />
            ) : (
              <GenericTable
                columns={columns}
                data={selectedSpecs}
                toolbarComponent={(props) => (
                  <SelectSpecValuesTableToolbar
                    {...props}
                    conditions={conditions}
                    deviceClasses={deviceClasses}
                    showMasterSpecsToggle={false}
                  />
                )}
              />
            )}
          </CardContent>
        </Card>
      </div>

      <div className="pt-4 border-t bg-background">
        <div className="flex justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={() => setStep(1)}
            disabled={isCreatingSpecList}
          >
            Back to Basic Info
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit || isCreatingSpecList}>
              Create Project Spec List ({selectedSpecs.filter((s) => s.selected).length} specs)
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

type CreateSpecListFormProps = {
  formData: FormData;
  isFetchingSpecValues: boolean;
  masterSpecValues: SpecValue[];
  masterVersions: SpecListVersionMinimal[];
  onOpenChange: (open: boolean) => void;
  refetchSpecListsData: () => void;
  setChoice: (choice: "" | "derived" | "custom") => void;
  setFormData: (data: FormData) => void;
  setStep: (step: number) => void;
  step: number;
};
const CreateSpecListForm = ({
  formData,
  isFetchingSpecValues,
  masterSpecValues,
  masterVersions,
  onOpenChange,
  refetchSpecListsData,
  setChoice,
  setFormData,
  setStep,
  step,
}: CreateSpecListFormProps) => {
  const { trigger: createSpecList, isMutating: isCreatingSpecList } = useCreateSpecList();
  const { data: specListsForMask = [] } = useSpecListsForSpecificMask(formData.maskId);
  const existingSpecListNames = specListsForMask.map((sl) => sl.name);

  const [selectedSpecs, setSelectedSpecs] = useState<SelectedSpecValue[]>([]);
  const previousMasterSpecValuesRef = useRef<SpecValue[]>([]);

  useEffect(() => {
    if (
      formData.masterVersionId &&
      masterSpecValues.length > 0 &&
      !deepEqual(previousMasterSpecValuesRef.current, masterSpecValues)
    ) {
      const custom = masterSpecValues.map((spec) => ({
        ...spec,
        selected: false,
        customValues: undefined,
      }));
      setSelectedSpecs(custom);
      previousMasterSpecValuesRef.current = masterSpecValues;
    }
  }, [formData.masterVersionId, masterSpecValues]);

  const applyCustomValuesToSpecValue = (specValue: SelectedSpecValue): RawSpecValue => {
    const customValueKeys = Object.keys(
      specValue.customValues ?? {},
    ) as unknown as (keyof SpecValue)[];

    const updatedSpecValue = { ...specValue };

    customValueKeys.forEach((key) => {
      const valueToApply = specValue.customValues![key];
      // @ts-expect-error not sure why it complains about a "never" type here
      updatedSpecValue[key] = valueToApply;
    });

    return {
      id: updatedSpecValue.id,
      spec_id: updatedSpecValue.specId,
      spec_version_id: updatedSpecValue.specListVersionId,
      jmp_yinc: updatedSpecValue.jmpYinc,
      jmp_ymax: updatedSpecValue.jmpYmax,
      jmp_ymin: updatedSpecValue.jmpYmin,
      lib_mean: updatedSpecValue.libMean,
      lib_median: updatedSpecValue.libMedian,
      lib_n: updatedSpecValue.libN,
      lib_q10: updatedSpecValue.libQ10,
      lib_q90: updatedSpecValue.libQ90,
      lib_std: updatedSpecValue.libStd,
      lsl: updatedSpecValue.lsl,
      tgt: updatedSpecValue.tgt,
      usl: updatedSpecValue.usl,
      spec: {
        id: updatedSpecValue.spec.id,
        device_class: updatedSpecValue.spec.deviceClass,
        name: updatedSpecValue.spec.name,
        report_name: updatedSpecValue.spec.reportName,
        condition: updatedSpecValue.spec.condition,
        legacy_names: updatedSpecValue.spec.legacyNames,
        dut_names: updatedSpecValue.spec.dutNames,
        recipe: {
          id: "test",
          name: updatedSpecValue.spec.recipe?.name ?? "",
          description: "",
        },
      },
    };
  };

  const handleSubmit = async (e: FormEvent) => {
    try {
      e.preventDefault();

      const selectedSpecsData = selectedSpecs.filter((spec) => spec.selected);
      const specValuesToPersist = selectedSpecsData.map(applyCustomValuesToSpecValue);

      await createSpecList(
        {
          mask_id: formData.maskId,
          master_version_id: formData.masterVersionId,
          name: formData.name,
          spec_values: specValuesToPersist,
        },
        {
          onError: (error) => {
            toast.error(error.message);
          },
          onSuccess: () => {
            refetchSpecListsData();
            onOpenChange(false);
          },
        },
      );
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="flex-1 overflow-hidden">
      <form onSubmit={handleSubmit} className="h-full flex flex-col">
        {step === 1 && (
          <FormStepOne
            existingNames={existingSpecListNames}
            formData={formData}
            masterVersions={masterVersions}
            onOpenChange={onOpenChange}
            setChoice={setChoice}
            setFormData={setFormData}
            setStep={setStep}
          />
        )}

        {step === 2 && (
          <FormStepTwo
            formData={formData}
            isCreatingSpecList={isCreatingSpecList}
            isFetchingSpecValues={isFetchingSpecValues}
            onOpenChange={onOpenChange}
            selectedSpecs={selectedSpecs}
            setFormData={setFormData}
            setSelectedSpecs={setSelectedSpecs}
            setStep={setStep}
          />
        )}
      </form>
    </div>
  );
};

type DerivedSpecListFormProps = {
  maskId: string;
  onOpenChange: (open: boolean) => void;
  refetchSpecListsData: () => void;
  setChoice: (choice: "" | "derived" | "custom") => void;
  specLists: SpecListMinimal[];
};
export function DerivedSpecListForm({
  maskId,
  onOpenChange,
  refetchSpecListsData,
  setChoice,
  specLists,
}: DerivedSpecListFormProps) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: "",
    maskId,
    masterVersionId: "",
  });

  const { error, isLoading, specValues } = useSpecValuesAvailableForMask(
    formData.masterVersionId,
    formData.maskId,
  );

  const allLatestMasterVersions = useMemo(() => {
    return specLists
      .filter((ms) => !ms.masterVersion && !ms.isArchived && ms.type === "master")
      .map((ms) => ms.versions[ms.versions.length - 1]);
  }, [specLists]);

  if (error) {
    return (
      <ModalContentError
        onOpenChange={onOpenChange}
        className="md:max-w-[80%] lg:w-[90%] lg:max-w-[1400px] mx-auto"
        description={error.message}
      />
    );
  }

  return (
    <>
      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
        <div className={`flex items-center gap-2 ${step >= 1 ? "text-blue-600" : ""}`}>
          <div
            className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${step >= 1 ? "bg-blue-600 text-white" : "bg-muted"}`}
          >
            1
          </div>
          Basic Info
        </div>
        <div className="w-8 h-px bg-muted" />
        <div className={`flex items-center gap-2 ${step >= 2 ? "text-blue-600" : ""}`}>
          <div
            className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${step >= 2 ? "bg-blue-600 text-white" : "bg-muted"}`}
          >
            2
          </div>
          Select Specs and Adjust Values
        </div>
      </div>
      <div className="flex-1 overflow-hidden">
        <CreateSpecListForm
          formData={formData}
          isFetchingSpecValues={isLoading}
          masterSpecValues={specValues}
          masterVersions={allLatestMasterVersions}
          onOpenChange={onOpenChange}
          refetchSpecListsData={refetchSpecListsData}
          setChoice={setChoice}
          setFormData={setFormData}
          setStep={setStep}
          step={step}
        />
      </div>
    </>
  );
}
